/**
 * Small typed DOM helpers.
 *
 * Every element is created here and kept in a closure, so nothing is ever looked up by id and
 * nothing needs a non-null assertion.
 */

export function el<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  className: string,
  text = '',
): HTMLElementTagNameMap[K] {
  const element = document.createElement(tag);
  if (className !== '') element.className = className;
  if (text !== '') element.textContent = text;
  return element;
}

/** Writes only when the value changed, so a 60 Hz update does not thrash layout. */
export function setText(element: HTMLElement, value: string): void {
  if (element.textContent !== value) element.textContent = value;
}

export function setClass(element: HTMLElement, name: string, on: boolean): void {
  if (element.classList.contains(name) !== on) element.classList.toggle(name, on);
}

/**
 * Writes a style property only when it changed.
 *
 * The guard `setText` and `setClass` already have, for the one kind of write that did not. A HUD
 * element is handed a view sixty times a second and most of them change nothing, and an unguarded
 * `style` write invalidates layout whether or not the value differs.
 *
 * Reading the property back off the element is the comparison rather than a copy kept beside it:
 * `style` is the element's own declarations, so reading it costs no layout and cannot fall out of
 * step with what was written.
 */
export function setStyleValue(element: HTMLElement, name: string, value: string): void {
  if (element.style.getPropertyValue(name) !== value) element.style.setProperty(name, value);
}

export function setVisible(element: HTMLElement, visible: boolean): void {
  setClass(element, 'is-hidden', !visible);
}

/** Formats a fraction as a whole percentage. */
export function percent(fraction: number): string {
  return `${String(Math.round(fraction * 100))}%`;
}

/** Formats seconds with one decimal, which is the resolution a drill timer needs. */
export function seconds(value: number): string {
  return `${value.toFixed(1)}s`;
}
